import React, { useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { NumField } from "../common/NumField";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import "../common/common.scss";
import "./MachineryInventory.scss";

const tomar = (inst) => ({ machinery: (inst.machinery || []).map((m) => ({ ...m, mediciones: [...(m.mediciones || [])] })) });
const fmt = (v, d = 0) => P.fmt(v, d);
const REGIMEN = [
  ["kW", "energy_performance_mach_kw"],
  ["unidades", "energy_performance_mach_units"],
  ["hs_dia", "energy_performance_mach_hours_day"],
  ["dias_anio", "energy_performance_mach_days_year"],
  ["fc", "energy_performance_mach_load_factor"],
];

// Alta de un equipo: desde la biblioteca (solo plantas textiles) se muestra el rango de referencia como orientación,
// pero NO se precarga ningún valor (hoja 01, fila 88): los datos vacíos dejan el equipo «Pendiente».
function AltaEquipo({ textil, lineas, onAgregar, t }) {
  const [idx, setIdx] = useState("");
  const ref = idx === "" ? null : P.MAQ_LIB[Number(idx)];
  const categorias = [...new Set(P.MAQ_LIB.map((m) => m.cat))];
  const agregar = () => {
    const nuevo = { ...P.ROW_TEMPLATES.machinery(), linea: lineas[0] || "" };
    if (ref) nuevo.nombre = ref.nombre;
    onAgregar(nuevo);
    setIdx("");
  };
  return (
    <div className="ep-mach__add">
      {textil && (
        <div className="ep-mach__lib">
          <label htmlFor="mach-lib">{t("energy_performance_mach_from_library")}</label>
          <select id="mach-lib" value={idx} onChange={(e) => setIdx(e.target.value)}>
            <option value="">{t("energy_performance_mach_manual")}</option>
            {categorias.map((c) => (
              <optgroup key={c} label={c}>
                {P.MAQ_LIB.map((m, i) => (m.cat === c ? <option key={i} value={i}>{m.nombre}</option> : null))}
              </optgroup>
            ))}
          </select>
          {ref && (
            <p className="ep-section__note">
              <strong>{ref.nombre}</strong> — {t("energy_performance_mach_reference")}: {ref.kW[0]}–{ref.kW[1]} kW · {t("energy_performance_mach_load_factor")} {ref.fc[0]}–{ref.fc[1]} · {ref.hs} h/{t("energy_performance_day")}.{" "}
              {P.ORIGEN_LBL[ref.origen]}. {ref.nota} <em>{t("energy_performance_mach_no_prefill")}</em>
            </p>
          )}
        </div>
      )}
      <Button type="button" basic size="small" onClick={agregar}><Icon name="plus" /> {t("energy_performance_mach_add")}</Button>
    </div>
  );
}

function Biblioteca({ t }) {
  const [cat, setCat] = useState("");
  const categorias = [...new Set(P.MAQ_LIB.map((m) => m.cat))];
  const filas = P.MAQ_LIB.filter((m) => !cat || m.cat === cat);
  const uso = (m) => (P.USO_FINAL.find((z) => z.id === P.usoFinalDe({ nombre: m.nombre, proceso: m.cat })) || P.USO_FINAL[0]).nombre.split(" (")[0];
  return (
    <Section title={t("energy_performance_mach_library_title").replace("{n}", P.MAQ_LIB.length)} note={t("energy_performance_mach_library_note")}>
      <label htmlFor="lib-cat" className="ep-section__note">{t("energy_performance_mach_filter_stage")} </label>
      <select id="lib-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
        <option value="">{t("energy_performance_all")} ({P.MAQ_LIB.length})</option>
        {categorias.map((c) => <option key={c}>{c}</option>)}
      </select>
      <div className="ep-table">
        <table>
          <thead>
            <tr><th>{t("energy_performance_mach_machine")}</th><th>{t("energy_performance_end_use")}</th><th>kW</th><th>{t("energy_performance_mach_load_factor")}</th><th>h/{t("energy_performance_day")}</th><th>{t("energy_performance_data_origin")}</th><th>{t("energy_performance_note")}</th></tr>
          </thead>
          <tbody>
            {filas.map((m, i) => (
              <tr key={i}>
                <td><strong>{m.nombre}</strong><div className="ep-section__note">{m.cat}</div></td>
                <td>{uso(m)}</td>
                <td className="ep-mono">{m.kW[0]}–{m.kW[1]}</td>
                <td className="ep-mono">{m.fc[0]}–{m.fc[1]}</td>
                <td className="ep-mono">{m.hs}</td>
                <td>{P.ORIGEN_LBL[m.origen] ? P.ORIGEN_LBL[m.origen].split(" (")[0] : m.origen}</td>
                <td className="ep-mach__libnote">{m.nota}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// Inventario de maquinaria (hoja 02 «maquinaria»; hoja 01 filas 49, 56, 88, 116).
export function MachineryInventory({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [aBorrar, setABorrar] = useState(null);

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;

  const textil = P.esTextil({ ...inst });
  const lista = b.machinery;
  const set = (i, clave, valor) => cambiar("machinery", lista.map((m, j) => (j === i ? { ...m, [clave]: valor } : m)));
  const seus = (inst.seus || []).map((u) => u.name);
  const fuentes = [...new Set(["Electricidad", ...(inst.fuentes || []).map((x) => x[0]).filter(Boolean), ...(inst.combustibles || []).map((c) => c.nombre).filter(Boolean)])];
  const lineas = [...new Set(lista.map((m) => m.linea || ""))];
  const pendientes = lista.filter((m) => P.estimarConsumo(m) == null).length;

  const tarjeta = (m, i) => {
    const kwh = P.estimarConsumo(m);
    const usoDetectado = P.usoFinalDe({ ...m, uso: "" });
    const texto = (clave, etiqueta, extra = {}) => (
      <div>
        <label htmlFor={`m${i}-${clave}`}>{etiqueta}</label>
        <input id={`m${i}-${clave}`} type="text" value={m[clave] || ""} onChange={(e) => set(i, clave, e.target.value)} {...extra} />
      </div>
    );
    return (
      <div className="ep-mach__card" key={m.id || i}>
        <div className="ep-mach__head">
          <input type="text" className="ep-mach__name" aria-label={t("energy_performance_mach_name")} value={m.nombre || ""} onChange={(e) => set(i, "nombre", e.target.value)} />
          {kwh == null
            ? <StatusBadge estado="Pendiente">{t("energy_performance_pending")}: {t("energy_performance_mach_missing_data")}</StatusBadge>
            : <span className="ep-mono ep-mach__kwh">{fmt(kwh)} kWh/{t("energy_performance_year")}</span>}
          <Button type="button" icon basic size="mini" aria-label={t("energy_performance_delete_row")} onClick={() => setABorrar(i)}><Icon name="trash alternate outline" /></Button>
        </div>
        <div className="ep-grid">
          {texto("linea", t("energy_performance_mach_line"))}
          {texto("proceso", t("energy_performance_process"))}
          <div>
            <label htmlFor={`m${i}-fuente`}>{t("energy_performance_mach_source")}</label>
            <input id={`m${i}-fuente`} type="text" list="ep-fuentes" value={m.fuente || ""} onChange={(e) => set(i, "fuente", e.target.value)} />
          </div>
          <div>
            <label htmlFor={`m${i}-seu`}>{t("energy_performance_mach_seu")}</label>
            <input id={`m${i}-seu`} type="text" list="ep-seus" value={m.seu || ""} onChange={(e) => set(i, "seu", e.target.value)} />
          </div>
          <div>
            <label htmlFor={`m${i}-uso`}>{t("energy_performance_end_use")}</label>
            <select id={`m${i}-uso`} value={m.uso || ""} onChange={(e) => set(i, "uso", e.target.value)}>
              <option value="">{t("energy_performance_detect")} ({(P.USO_FINAL.find((z) => z.id === usoDetectado) || {}).nombre})</option>
              {P.USO_FINAL.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </div>
          {texto("potencia", t("energy_performance_mach_power_text"))}
          {texto("placa", t("energy_performance_mach_nameplate"))}
          <div className="ep-grid__wide">
            <label htmlFor={`m${i}-proposito`}>{t("energy_performance_mach_purpose")}</label>
            <input id={`m${i}-proposito`} type="text" value={m.proposito || ""} onChange={(e) => set(i, "proposito", e.target.value)} />
          </div>
          {REGIMEN.map(([clave, etiqueta]) => (
            <div key={clave}>
              <label>{t(etiqueta)}</label>
              <NumField value={m[clave]} campo={clave} ariaLabel={`${t(etiqueta)} — ${m.nombre}`} onChange={(v) => set(i, clave, v)} onError={(e) => marcarInvalidos(`${i}.${clave}`, e ? 1 : 0)} />
            </div>
          ))}
          <div className="ep-grid__wide">
            <label htmlFor={`m${i}-med`}>{t("energy_performance_mach_measurements")}</label>
            <textarea id={`m${i}-med`} rows={2} value={m.mediciones.join("\n")} onChange={(e) => set(i, "mediciones", e.target.value.split("\n"))} />
          </div>
        </div>
      </div>
    );
  };

  const onSave = () => guardar({ machinery: lista.map((m) => ({ ...m, mediciones: m.mediciones.map((x) => x.trim()).filter(Boolean), uso: m.uso || undefined })) });

  return (
    <div className="ep-screen ep-mach" key={version}>
      <h3 className="ep-screen__title">{t("energy_performance_mach_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_mach_intro")}</p>
      {error && <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>}
      {pendientes > 0 && <Notice>{t("energy_performance_mach_pending_note").replace("{n}", pendientes)}</Notice>}
      {f && P.equiposSinCategoria(f).length > 0 && <Notice tipo="info">{t("energy_performance_mach_uncategorized").replace("{n}", P.equiposSinCategoria(f).length)}</Notice>}

      <datalist id="ep-fuentes">{fuentes.map((x) => <option key={x} value={x} />)}</datalist>
      <datalist id="ep-seus">{[...seus, t("energy_performance_general_services")].map((x) => <option key={x} value={x} />)}</datalist>

      {lineas.map((linea) => (
        <div key={linea || "_"} className="ep-mach__group">
          <h4 className="ep-mach__group-title">{linea || t("energy_performance_mach_no_line")}</h4>
          {lista.map((m, i) => ((m.linea || "") === linea ? tarjeta(m, i) : null))}
        </div>
      ))}
      {!lista.length && <p className="ep-section__note">{t("energy_performance_mach_empty")}</p>}

      <AltaEquipo textil={textil} lineas={inst.lines || []} onAgregar={(m) => cambiar("machinery", [...lista, m])} t={t} />

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={onSave} onDiscard={descartar} t={t} />

      {textil && <Biblioteca t={t} />}

      <Confirm
        open={aBorrar != null}
        content={t("energy_performance_confirm_delete_row")}
        cancelButton={t("cancel")}
        confirmButton={t("energy_performance_delete")}
        onCancel={() => setABorrar(null)}
        onConfirm={() => { cambiar("machinery", lista.filter((_, j) => j !== aBorrar)); setABorrar(null); }}
      />
    </div>
  );
}
